import type { LauchMode } from '@healthhub/message-channel';
import { COMMON_MESSAGE_ACTION, COMMON_MESSAGE_ACTIONS_KEYS } from '@healthhub/message-channel';
import {
  addQueryParamsToUrl,
  addQueryParamStringToUrl,
  dayjsWithTimezone,
  generateCalendarFile,
  navigate,
} from '@healthhub/react-ui';

import { ACTION_BACK_PATH } from '@shared/constants/paths';
import { LayoutViewBridge } from '@shared/context';

const ACTION_RELOAD_HEALTH_PROFILE = 'action=reloadhealthprofile';
const ACTION_RELOAD_APPOINTMENT = 'action=reloadAppointment';
const ACTION_EXTERNAL_LINK = 'action=externallink';

const MESSAGE_ACTIONS_KEYS = {
  ...COMMON_MESSAGE_ACTIONS_KEYS,
  downloadSchoolHealthPdf: 'downloadSchoolHealthPdf',
  edInstitution: 'edInstitution',
  reloadRelatedPeople: 'reloadRelatedPeople',
  reloadAppointment: 'reloadAppointment',
  reloadUserProfile: 'reloadUserProfile',
  reloadHealthProfile: 'reloadHealthProfile',
  loadPaymentStatus: 'loadPaymentStatus',
  launchPaymentGateway: 'launchPaymentGateway',
  notifyBack: 'notifyBack',
} as const;

// Keys of values persisted in the native app's mobile store (mobileStoreGet/mobileStoreSet)
const MOBILE_STORE_KEYS = {
  myInfoSyncSuccessRedirectPath: 'myInfoSyncSuccessRedirectPath',
} as const;

// Use camelCase for HH+, BAU use previous one
const MESSAGE_ACTION = {
  ...COMMON_MESSAGE_ACTION,

  // (dismiss one layer of the native webview)
  back: (data?: { notify?: boolean }) => {
    const { isMobileView } = LayoutViewBridge.getLayoutViewBridge?.() ?? {};

    if (!isMobileView) {
      navigate(ACTION_BACK_PATH);
      return undefined;
    }

    // TODO: Update message channel common to handle notify param
    return {
      action: MESSAGE_ACTIONS_KEYS.back,
      data,
    };
  },

  // Calendar action to add event
  calendar: (data: {
    description?: string;
    endDate?: string;
    layoutView?: {
      isMobileView: boolean;
      isSharedServiceView: boolean;
    };
    location?: string;
    path?: string;
    startDate: string;
    title: string;
  }) => {
    const { startDate, endDate, location = '', title = '', description = '', path, layoutView } = data;
    const { isSharedServiceView, isMobileView } = (layoutView || LayoutViewBridge.getLayoutViewBridge?.()) ?? {};

    const startDateinDayjs = dayjsWithTimezone(startDate);
    const startDateNumber = startDateinDayjs.valueOf();
    const endDateinDayjs = endDate ? dayjsWithTimezone(endDate) : startDateinDayjs.add(30, 'minute');
    const endDateNumber = endDateinDayjs.valueOf();

    const locationEncode = encodeURIComponent(location);
    const titleEncode = encodeURIComponent(title);
    const descriptionEncode = encodeURIComponent(description);

    if (isSharedServiceView) {
      const calendarUrl = `${path}?action=calendar&title=${titleEncode}&description=${descriptionEncode}&startTime=${startDateNumber}&endTime=${endDateNumber}&location=${locationEncode}`;
      navigate(calendarUrl);
      return undefined;
    }

    if (!isMobileView) {
      generateCalendarFile({ description, endDate, location, startDate, summary: title });
      return undefined;
    }

    return COMMON_MESSAGE_ACTION.calendar({
      description,
      endDate: endDateinDayjs.format(),
      location,
      path,
      startDate: startDateinDayjs.format(),
      title,
    });
  },

  // Open external link in external browser
  externalLink: (data: { inApp?: boolean; url: string }) => {
    const { url } = data;
    const { isPortalView, isMobileView } = LayoutViewBridge.getLayoutViewBridge?.() ?? {};

    if (isPortalView) {
      window.open(url, '_blank', 'noopener,noreferrer');

      return undefined;
    }

    if (!isMobileView) {
      navigate(addQueryParamStringToUrl(url, ACTION_EXTERNAL_LINK));
      return undefined;
    }

    return COMMON_MESSAGE_ACTION.externalLink(data);
  },

  // Navigate to a native screen/service via its service catalog code
  navigation: (data: {
    attributes?: {
      launchMode?: LauchMode;
      params?: Record<string, any>;
      path?: string;
    };
    code: string;
  }) => {
    const codeList = {
      healthProfile: 'healthProfile',
    };
    const { isSharedServiceView } = LayoutViewBridge.getLayoutViewBridge?.() ?? {};
    const { code } = data;

    if (isSharedServiceView) {
      if (code === codeList.healthProfile) {
        navigate(`?${ACTION_RELOAD_HEALTH_PROFILE}`);
      }

      return undefined;
    }

    return COMMON_MESSAGE_ACTION.navigation(data);
  },

  // Trigger share function in mobile
  share: (data: { url: string }) => {
    const { isMobileView } = LayoutViewBridge.getLayoutViewBridge?.() ?? {};
    const { url } = data;

    if (!isMobileView) {
      window.location.href = addQueryParamsToUrl(window.location.href, {
        action: 'share',
        url: encodeURIComponent(url),
      });
      return undefined;
    }

    return COMMON_MESSAGE_ACTION.share(data);
  },

  // Will need to keep this due to channel app support
  // HH app will not need to use this action anymore
  unauthorised: (): any => {
    const UNAUTHORISED_PATH = '?action=unauthorised';
    const { isMobileView } = LayoutViewBridge.getLayoutViewBridge?.() ?? {};

    if (!isMobileView) {
      navigate(UNAUTHORISED_PATH);
      return undefined;
    }

    COMMON_MESSAGE_ACTION.unauthorised();
    return undefined;
  },

  // School health download pdf action
  downloadSchoolHealthPdf: (data: { memberPrn: string; recordId: string; recordType: string }) => ({
    action: MESSAGE_ACTIONS_KEYS.downloadSchoolHealthPdf,
    data,
  }),

  // Reload appointment after booking/cancelling
  reloadAppointment: () => {
    const { isMobileView, isPortalView } = LayoutViewBridge.getLayoutViewBridge?.() ?? {};

    if (isPortalView) {
      return undefined;
    }

    if (!isMobileView) {
      if (!window.location.search.includes(ACTION_RELOAD_HEALTH_PROFILE)) {
        navigate(`?${ACTION_RELOAD_APPOINTMENT}`);
      }

      return undefined;
    }

    return {
      action: MESSAGE_ACTIONS_KEYS.reloadAppointment,
    };
  },
  // Launch native payment gateway to process a payment
  launchPaymentGateway: () => ({
    action: MESSAGE_ACTIONS_KEYS.launchPaymentGateway,
  }),
  // Reload user profile after profile-related updates
  reloadUserProfile: () => ({
    action: MESSAGE_ACTIONS_KEYS.reloadUserProfile,
  }),
  // Reload related people (family/caregivers) list after add/edit/remove
  reloadRelatedPeople: () => ({
    action: MESSAGE_ACTIONS_KEYS.reloadRelatedPeople,
  }),
} as const;

export { MESSAGE_ACTION, MESSAGE_ACTIONS_KEYS, MOBILE_STORE_KEYS };
